import { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, CircularProgress, Alert, Container } from '@mui/material';
import { useParams } from 'react-router-dom';
import Navbar from '../../components/Navbar';
import RequireAuth from '../../components/athletes/RequireAuth';
import { useAuth } from '../../context/AuthContext';
import { getTeam, listTeamStaff } from '../../services/teamService';
import { listTeamRoster } from '../../services/rosterService';
import { listTestingSessions } from '../../services/athleteService';
import {
  recordTeamTestAttempt, changeTestOccurrenceMax, endTeamTestOccurrenceEarly, reopenTeamTestOccurrence,
} from '../../services/teamTestingService';
import { getTeamSessionLeaderboard, getTeamSeasonLeaderboard } from '../../services/teamLeaderboardService';
import { hasProgramManagementAuthorityForTeam } from '../../services/programService';
import {
  listTeamAvailableTestTypes, listMyCoachTestTypes, createCoachTestType, updateCoachTestType,
} from '../../services/testTypeService';
import TeamHubLayout from '../../components/teams/TeamHubLayout';
import TeamTestingStation from '../../components/teams/TeamTestingStation';

// Phase C.7a — thin route wrapper, same discipline as every other Team
// page this session: resolves route params + loads data, then hands
// everything to the pure, router-free TeamTestingStation component.
// fetchAthleteSessions/recordAttempt are passed down as plain functions
// (not imported directly by the Station) so that component stays
// testable with simple mocks, independent of the real service layer.
function TeamTestingInner() {
  const { teamId } = useParams();
  const { currentUser } = useAuth();
  const [team, setTeam] = useState(null);
  const [roster, setRoster] = useState([]);
  const [testTypes, setTestTypes] = useState([]);
  const [myTests, setMyTests] = useState([]);
  const [myRoles, setMyRoles] = useState([]);
  const [hasTeamAuthority, setHasTeamAuthority] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Coach-Owned Test Types (058) — every Team, Program-affiliated or
  // not, now resolves its available catalog identically: FFN tests plus
  // this Team's own currently-active Head Coach(es)' custom tests, via
  // the canonical server-side resolver (list_team_available_test_types).
  // The old team.program_id branch (Program-scoped catalog vs a curated
  // fallback) is gone — Program affiliation was never actually relevant
  // to FFN-test availability, and there is no Program-owned test
  // category in the new product model at all.
  const load = useCallback(async () => {
    const [t, staff, authority, r, types, mine] = await Promise.all([
      getTeam(teamId), listTeamStaff(teamId), hasProgramManagementAuthorityForTeam(teamId),
      listTeamRoster(teamId), listTeamAvailableTestTypes(teamId),
      currentUser?.id ? listMyCoachTestTypes(currentUser.id) : Promise.resolve([]),
    ]);
    setTeam(t);
    setMyRoles(staff.filter(s => s.user_id === currentUser?.id && s.status === 'active').map(s => s.role));
    setHasTeamAuthority(authority);
    setRoster(r);
    setTestTypes(types);
    setMyTests(mine);
  }, [teamId, currentUser?.id]);

  useEffect(() => {
    load().catch(e => setError(e.message)).finally(() => setLoading(false));
  }, [load]);

  // Same authority tier record_team_test_attempt() (049) itself
  // independently re-verifies server-side — Team Admin/Head Coach of
  // THIS team, or Program-level authority over it. Assistant Coach and
  // guardians deliberately do not satisfy this, matching every other
  // Team-management action in this app; the RPC enforces it regardless
  // of what this computes.
  const canConductTesting = useMemo(
    () => hasTeamAuthority || myRoles.includes('team_admin') || myRoles.includes('head_coach'),
    [hasTeamAuthority, myRoles]
  );
  // 054 PRE-APPLICATION REVIEW — KNOWN, ACCEPTED, DEFERRED MISMATCH:
  // get_team_session_leaderboard()/get_team_season_leaderboard() (054)
  // authorize assistant_coach for READ access, matching 049's own
  // existing read-policy breadth — but canConductTesting above (and
  // therefore this entire page, capture UI included) has never admitted
  // assistant_coach, by the same deliberate design as
  // record_team_test_attempt()'s own WRITE-only team_admin/head_coach
  // check. Widening canConductTesting to let an assistant_coach reach
  // the leaderboard would ALSO hand them full Team Testing capture
  // authority as a side effect, since this page has no separate
  // read-only mode today — that would be widening WRITE authority merely
  // to expose a READ surface, which this phase explicitly does not do.
  // The RPCs' broader read authorization is intentionally left
  // "available but unused by any UI yet" — safe, forward-compatible
  // headroom for a future dedicated read-only Team Testing surface, not
  // a bug to fix now.

  // Coach-Owned Test Types (058) — the CONTEXTUAL gate for showing "My
  // Tests" on THIS team's page: is the viewer currently a Head Coach of
  // THIS team. Deliberately narrower than create_coach_test_type()'s own
  // server-side floor (any active head_coach role, anywhere) — creation
  // is meant to happen contextually, from the team a coach is actually
  // looking at; the RPC remains the real authority regardless.
  const canManageTests = useMemo(() => myRoles.includes('head_coach'), [myRoles]);

  const fetchAthleteSessions = useCallback((athleteId) => listTestingSessions(athleteId), []);
  const recordAttempt = useCallback((params) => recordTeamTestAttempt(params), []);
  // Testing Occurrence / Attempt Protocol (059) — same injected-callback
  // shape as recordAttempt above; no new authorization prop is threaded
  // through here since TeamTestingStation is only ever rendered (below)
  // once canConductTesting is already true, the exact same tier these
  // three RPCs independently re-verify server-side.
  const handleEndTestEarly = useCallback((params) => endTeamTestOccurrenceEarly(params), []);
  const handleChangeOccurrenceMax = useCallback((params) => changeTestOccurrenceMax(params), []);
  const handleReopenOccurrence = useCallback((params) => reopenTeamTestOccurrence(params), []);
  // Coach-Owned Test Types (058) — both wrappers reload the FULL page
  // dataset (not just testTypes/myTests) after a successful write,
  // matching this page's own single `load()` discipline elsewhere;
  // CoachTestManagerDialog awaits the returned promise and only then
  // returns to its list view, so the dialog's own re-render already
  // reflects the freshly-reloaded myTests/testTypes props.
  const handleCreateTest = useCallback(async (payload) => {
    const created = await createCoachTestType(payload);
    await load();
    return created;
  }, [load]);
  const handleUpdateTest = useCallback(async (payload) => {
    const updated = await updateCoachTestType(payload);
    await load();
    return updated;
  }, [load]);
  // TEAM TESTING LEADERBOARDS (054) — teamId is closed over here, never
  // passed back up from the Station, matching fetchAthleteSessions/
  // recordAttempt's own injected-callback shape exactly.
  const fetchSessionLeaderboard = useCallback(
    (testType, testDate) => getTeamSessionLeaderboard({ teamId, testType, testDate }),
    [teamId]
  );
  const fetchSeasonLeaderboard = useCallback(
    (testType) => getTeamSeasonLeaderboard({ teamId, testType }),
    [teamId]
  );

  if (loading) return <Box sx={{ py: 10, textAlign: 'center' }}><CircularProgress sx={{ color: '#2ea84b' }} /></Box>;
  if (error) return <Container maxWidth="sm" sx={{ py: 6 }}><Alert severity="error">{error}</Alert></Container>;
  if (!team) return null;

  return (
    <TeamHubLayout team={team} active="testing">
      {!canConductTesting ? (
        <Alert severity="info">
          Only a Team Admin, Head Coach, or Program-authorized manager can conduct Team Testing for this team.
        </Alert>
      ) : testTypes.length === 0 ? (
        <Alert severity="info">No active test types are configured yet.</Alert>
      ) : (
        <TeamTestingStation
          team={team}
          roster={roster}
          testTypes={testTypes}
          fetchAthleteSessions={fetchAthleteSessions}
          recordAttempt={recordAttempt}
          fetchSessionLeaderboard={fetchSessionLeaderboard}
          fetchSeasonLeaderboard={fetchSeasonLeaderboard}
          canManageTests={canManageTests}
          myTests={myTests}
          onCreateTest={handleCreateTest}
          onUpdateTest={handleUpdateTest}
          onEndTestEarly={handleEndTestEarly}
          onChangeOccurrenceMax={handleChangeOccurrenceMax}
          onReopenOccurrence={handleReopenOccurrence}
        />
      )}
    </TeamHubLayout>
  );
}

export default function TeamTestingPage() {
  return (
    <Box sx={{ minHeight: '100vh' }}>
      <Navbar />
      <RequireAuth><TeamTestingInner /></RequireAuth>
    </Box>
  );
}
